import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { GenericFormScene } from 'shared-ui/lib/components/generic-form-scene/GenericFormScene';
import { setupQueryConfig } from 'shared-ui/lib/config/query.config';
import { parseGraphqlErrors } from 'shared-ui/lib/utils/get-gql-error';
import { useParams } from 'shared-ui/lib/utils/use-params';
import { ServiceEventAttachmentsModal } from '../../components/scenes/service-event/ServiceEventAttachmentsModal';
import { routesConfig } from '../../config/routes.config';
import {
  serviceEventFormAll,
  serviceEventFormConsumer,
  ServiceEventFormBody,
} from '../../form/service-event/service-event';
import { useEditServiceEventQuery, useUpdateServiceEventMutation } from '../../generated/graphql';
import { useUploadFile } from '../../hooks/use-upload-file';
import { GraphQLError } from 'graphql';
import UserContext from '../../contexes/UserContext';

export const ServiceEventEditScene = (): JSX.Element => {
  const { t } = useTranslation('ServiceEventEditScene');
  const { t: tVehicle } = useTranslation('VehicleShowScene');
  const navigate = useNavigate();
  const { vehicleId, id } = useParams<{ vehicleId: string; id: string }>();
  const { uploadFile, isImageLoading } = useUploadFile({ type: 'service-event-image' });
  const [user] = useContext(UserContext);
  const [isAttachmentsOpen, setIsAttachmentsOpen] = useState(false);

  const [updateServiceEvent, { loading }] = useUpdateServiceEventMutation();

  const query = useEditServiceEventQuery(setupQueryConfig({ id }));

  const handleSubmit = async ({
    photo,
    type,
    name,
    serviceDate,
    mileage,
    runningHours,
    description,
  }: ServiceEventFormBody) => {
    const { data, errors } = await updateServiceEvent({
      variables: {
        id,
        body: { type, name, serviceDate, mileage, runningHours, description },
      },
    });
    if (!data) throw new Error(parseGraphqlErrors(errors as GraphQLError[]));
    await uploadFile(photo, data.updateServiceEvent.id);
    navigate(routesConfig.SERVICE_EVENTS.SHOW.replace(':id', id).replace(':vehicleId', vehicleId));
  };

  const isConsumerUser = user.type === 'consumer' && !user.isBusinessAccountOwner;

  const form = isConsumerUser ? serviceEventFormConsumer : serviceEventFormAll;

  const se = query.data?.serviceEvent;
  const preFooterElement =
    se != null ? (
      <>
        <div className="max-w-screen-lg mx-auto mb-4 flex items-center space-x-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-primary flex-shrink-0"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
          </svg>
          <button
            type="button"
            onClick={() => setIsAttachmentsOpen(true)}
            className="text-primary hover:text-secondary text-sm font-medium underline"
          >
            {tVehicle('attachmentsLink', { count: se.attachments.length })}
          </button>
        </div>
        <ServiceEventAttachmentsModal
          isOpen={isAttachmentsOpen}
          onClose={() => setIsAttachmentsOpen(false)}
          serviceEventId={id}
          attachments={se.attachments}
          hasActiveLicense={se.vehicle.hasActiveLicense}
          attachmentsActionsLocked={se.isTimeLocked}
          onRefetch={() => {
            void query.refetch();
          }}
        />
      </>
    ) : null;

  return (
    <GenericFormScene
      type="edit"
      title={t('editServiceEvent')}
      query={query}
      queryDataPath="serviceEvent"
      onSubmit={handleSubmit}
      form={form}
      isSubmitLoading={loading || isImageLoading}
      backLink={routesConfig.VEHICLES.SHOW.replace(':id', vehicleId)}
      preFooterElement={preFooterElement}
    />
  );
};
